import {QlQueryDto} from '../data/dto/ql-query.dto';
import {QlQueryStartDto} from '../data/dto/ql-query-start.dto';
import {QlJoinDto} from '../data/dto/ql-join.dto';
import {QlQueryContentFieldDto} from '../data/dto/ql-query-content-field.dto';
import {PagingDto} from '../../dto/model/paging.dto';
import {Sorting} from '../../misc/sorting';
import {QlBuilderViolation} from './ql-builder-violation';
import {
    QlBuilderChildNode,
    QlBuilderEntityNode,
    QlBuilderExtendedNode,
    QlBuilderFieldModel,
    QlBuilderGroupByAggregateModel,
    QlBuilderGroupByFieldModel,
    QlBuilderGroupByModel,
    QlBuilderJoinNode,
    QlBuilderJoinRelation,
    QlBuilderJoinType,
    QlBuilderModel,
    QlBuilderNode,
    QlBuilderSideCarNode,
} from './ql-builder-model';
import type {QlBuilderFromQueryResult} from './interface/ql-builder-from-query-result.interface';
import type {QlBuilderDateFieldResolver} from './model/ql-builder-date-field-resolver.type';
import {FilterCriteria} from '../../filter/api/filter.criteria';
import {QlBuilderRelativeDate} from './ql-builder-relative-date';

/**
 * Re-exported from its own file ({@link QlBuilderFromQueryResult}) rather than redeclared here, so
 * every existing import of `ql-builder-query-converter` keeps working unchanged.
 */
export type {QlBuilderFromQueryResult} from './interface/ql-builder-from-query-result.interface';

/**
 * Numeric limits from requirement.md Policy "Grenzen" / api-contract.md "QL-Konventionen fuer den
 * Builder". The backend UiQlWidgetDefinitionPolicy stays authoritative — these are mirrored here
 * ONLY so toQlQuery never emits an obviously-over-limit definition and precheckQlQuery/fromQlQuery
 * can give immediate UI feedback.
 */
export const QL_BUILDER_POLICY_LIMITS = {
    maxJoinDepth: 3,
    /** Includes +/_ (extended) and side-car joins. */
    maxJoins: 8,
    maxFieldsPerNode: 30,
    /**
     * Spike S2 (api-contract.md "Spike-Ergebnisse (QL-1)"): every SECOND +/_ node fails in the
     * engine (`projectKey: Field '<as>.id' not found in record`) — so exactly one per definition.
     */
    maxExtendedNodes: 1,
    maxSideCarFields: 30,
    maxPagingEntity: 100,
    maxPagingGroupBy: 500,
    /** Preview page size (requirement.md Frontend: "Vorschau: ... Seitengroesse 25"). */
    defaultPreviewPageSize: 25,
} as const;

/** g_<typeFieldId>:value|id|objectTypeId|shortName|name — generic TypeField attribute (requirement.md G). */
const GENERIC_FIELD_PATTERN = /^g_(\d+):(value|id|objectTypeId|shortName|name)$/;

/** o_<attributeName>:value|id|objectTypeId|shortName|name — ObjectTypeAttribute / EAV (requirement.md G). */
const EAV_FIELD_PATTERN = /^o_[A-Za-z_][A-Za-z0-9_]*:(value|id|objectTypeId|shortName|name)$/;

/** The DTO/form-only namespace that must NEVER appear in a QL field name (requirement.md G). */
const GEN_DTO_NAME_PATTERN = /^gen_/;

/** Reverse 1:N join name `<Entity>#<fkAttribute>` — group 1 is the back-reference FK attribute. */
const REVERSE_JOIN_NAME_PATTERN = /^[^#]+#([A-Za-z_][A-Za-z0-9_]*)$/;

/** Signed-OUT polymorphic join naming (requirement.md "Weiter OUT" / Policy "Verboten"). */
const POLYMORPHIC_JOIN_PATTERN = /(^#objectId(#|$))|(objectId#)/;

/**
 * Pure, Angular-free translation between QlBuilderModel (the builder's editable tree) and the
 * backend wire format QlQueryDto (core/ql/dto/data/QlQueryDto|QlQueryStartDto|QlJoinDto, verified
 * against those Java sources directly — NOT against fe_core_ql/SKILL.md:120-150, which
 * requirement.md flags as describing joins incorrectly).
 *
 * Every QlQueryDto this converter EMITS satisfies, structurally:
 * - type: 'view' always (query type, not pipe type — orthogonal to the export-pipe type the
 *   UiQlWidgetDataBuilder chooses per requirement.md Spikes S1).
 * - Every join level uses type: 'groupBy' once the root is groupBy (never a mix).
 * - Paging is ALWAYS set, clamped to the policy limit for the mode.
 * - gen_<id> NEVER appears (only g_<id>:... is valid QL — requirement.md G).
 * - parameters NEVER appears anywhere (root or join) — it is not a field of QlBuilderModel at all,
 *   so there is nothing in the model that could produce it.
 */

/**
 * AI:
 * Status: "in progress"
 * Type: Service
 * SubType: DataProvider
 * Reason: Converts the QL-Widget-Builder model to/from the QlQueryDto that becomes the QL data
 *   provider request (dataSource=ql) a built WidgetData sends to /core/ql/query.
 */
export class QlBuilderQueryConverter {

    // ------------------------------------------------------------------
    // model -> QlQueryDto
    // ------------------------------------------------------------------

    static toQlQuery(model: QlBuilderModel): QlQueryDto {
        const isGroupBy = !!model.groupBy;

        const rootFields: QlQueryContentFieldDto[] = isGroupBy
            ? this.buildGroupByFields(model.groupBy!)
            : model.root.fields.map(f => this.buildFieldDto(f));

        const start: QlQueryStartDto = {
            type: isGroupBy ? 'groupBy' : 'entity',
            name: model.root.entityName,
            as: model.root.alias,
            fields: rootFields,
            joins: this.orderChildren(model.root.children).map(child => this.buildJoin(child, isGroupBy, 1)),
        };

        const maxPaging = isGroupBy
            ? QL_BUILDER_POLICY_LIMITS.maxPagingGroupBy
            : QL_BUILDER_POLICY_LIMITS.maxPagingEntity;
        const requestedPageSize = model.limit ?? QL_BUILDER_POLICY_LIMITS.defaultPreviewPageSize;
        const pageSize = Math.max(1, Math.min(requestedPageSize, maxPaging));

        const query: QlQueryDto = {
            name: 'definition',
            type: 'view',
            start,
            paging: PagingDto.create(0, pageSize),
        };

        // WHERE = the root's "Start" filters first, then the query-level conditions (all AND-combined by the engine)
        const filters = [...(model.root.filters ?? []), ...(model.conditions ?? [])];
        if (filters.length) {
            query.filters = filters;
        }
        if (model.sortings?.length) {
            query.sortings = this.rewriteSideCarSortings(model);
        }

        return query;
    }

    // ------------------------------------------------------------------
    // query-level filters: root "Start" shortcut vs. conditions over all entities
    // ------------------------------------------------------------------

    /**
     * `<alias>.<field>` → the alias; `null` for a field without exactly one dot (the engine resolves
     * query-level filter paths strictly as two parts — `a.b.c` is `unknownField`, a bare name too).
     */
    static filterAliasOf(field: string | null | undefined): string | null {
        if (!field) {
            return null;
        }
        const parts = field.split('.');
        return parts.length === 2 && parts[0] && parts[1] ? parts[0] : null;
    }

    /** Every leaf criterion (one with a field) of a criterion tree, depth first. */
    static leafCriteria(criterion: FilterCriteria | null | undefined): FilterCriteria[] {
        if (!criterion) {
            return [];
        }
        if (criterion.field) {
            return [criterion];
        }
        return [...(criterion.and ?? []), ...(criterion.or ?? [])].flatMap(member => this.leafCriteria(member));
    }

    /** A field-less criterion without a single leaf below it (the condition builder's empty wrapper/group). */
    static isEmptyCriterion(criterion: FilterCriteria | null | undefined): boolean {
        return !criterion || this.leafCriteria(criterion).length === 0;
    }

    /**
     * Whether a query-level criterion belongs to the root node's "Start" filters: a leaf on `<rootAlias>.…`
     * or a group (AND/OR, nested) whose EVERY leaf names the root alias. Anything that reaches into another
     * alias — or carries a leaf without an alias — is a query-level condition ({@link QlBuilderModel.conditions}).
     * Deterministic, so `fromQlQuery(toQlQuery(model))` splits exactly the way the model was built.
     */
    static isRootOnlyCriterion(criterion: FilterCriteria, rootAlias: string): boolean {
        const leaves = this.leafCriteria(criterion);
        return leaves.length > 0 && leaves.every(leaf => this.filterAliasOf(leaf.field) === rootAlias);
    }

    /**
     * Splits `QlQueryDto.filters` into the root node's filters and the query-level conditions
     * ({@link isRootOnlyCriterion}); empty groups are dropped. Order within each list is kept.
     */
    static splitQueryFilters(filters: FilterCriteria[] | null | undefined,
                             rootAlias: string): { rootFilters: FilterCriteria[]; conditions: FilterCriteria[] } {
        const rootFilters: FilterCriteria[] = [];
        const conditions: FilterCriteria[] = [];
        for (const criterion of filters ?? []) {
            if (this.isEmptyCriterion(criterion)) {
                continue;
            }
            (this.isRootOnlyCriterion(criterion, rootAlias) ? rootFilters : conditions).push(criterion);
        }
        return {rootFilters, conditions};
    }

    /**
     * Spike S7: a regular join declared AFTER a side-car join breaks the engine's GROUP BY
     * (`REGULAR_JOIN_AFTER_SIDE_CAR_ALLOWED = false`, policy code `sideCarSiblingJoin`), a regular
     * join BEFORE it works. The policy walks the definition depth first, so every regular join of
     * the WHOLE tree must come before the first side-car join. Stable order per level:
     * 1. children whose subtree carries no side-car (regular joins, extended leaves),
     * 2. regular joins whose subtree carries a side-car,
     * 3. side-car leaves.
     * Two sibling subtrees that BOTH carry a side-car cannot be ordered validly — the backend
     * policy (and precheckQlQuery) then reports `sideCarSiblingJoin`.
     */
    private static orderChildren(children: QlBuilderChildNode[]): QlBuilderChildNode[] {
        const rank = (child: QlBuilderChildNode): number => {
            if (child.kind === 'sideCar') {
                return 2;
            }
            return child.kind === 'join' && this.subtreeHasSideCar(child) ? 1 : 0;
        };
        return children
            .map((child, index) => ({child, index, rank: rank(child)}))
            .sort((a, b) => a.rank - b.rank || a.index - b.index)
            .map(entry => entry.child);
    }

    private static subtreeHasSideCar(node: QlBuilderNode): boolean {
        if (node.kind === 'sideCar') {
            return true;
        }
        if (node.kind === 'extended') {
            return false;
        }
        return node.children.some(child => this.subtreeHasSideCar(child));
    }

    /**
     * Spike S6: a side-car column is sorted by its RESULT key `<hostAs>.ext_<id>` — never by the
     * side-car join alias. A sorting that names the side-car alias (`<sideCarAs>.<id>` or
     * `<sideCarAs>.ext_<id>`) is rewritten to the host alias; every other sorting is kept as is.
     */
    private static rewriteSideCarSortings(model: QlBuilderModel): Sorting[] {
        const hostBySideCarAlias = new Map<string, string>();
        const walk = (node: QlBuilderEntityNode | QlBuilderJoinNode): void => {
            for (const child of node.children) {
                if (child.kind === 'sideCar') {
                    hostBySideCarAlias.set(child.alias, node.alias);
                } else if (child.kind === 'join') {
                    walk(child);
                }
            }
        };
        walk(model.root);

        return (model.sortings ?? []).map(sorting => {
            const dot = sorting.name?.indexOf('.') ?? -1;
            if (dot <= 0) {
                return sorting;
            }
            const host = hostBySideCarAlias.get(sorting.name.substring(0, dot));
            if (!host) {
                return sorting;
            }
            const column = sorting.name.substring(dot + 1).replace(/^ext_/, '');
            return new Sorting(`${host}.ext_${column}`, sorting.ascending, sorting.type);
        });
    }

    /**
     * Spike S5: an `onlyFirst` reverse join is linked to its parent by the back-reference FK
     * column, so the engine fails (`projectKey: Field '<as>.<fk>id' not found in record`) unless
     * the join selects `<fkAttribute>Id`. Derived from the MetaDataJoin name
     * `<Entity>#<fkAttribute>` (e.g. `xx.XxCarPilotCarPrice#car` → `carId`); `null` when the name
     * does not carry the FK attribute.
     */
    static onlyFirstForeignKeyField(joinName: string | null | undefined): string | null {
        const match = REVERSE_JOIN_NAME_PATTERN.exec(joinName ?? '');
        return match ? `${match[1]}Id` : null;
    }

    private static buildGroupByFields(groupBy: QlBuilderGroupByModel): QlQueryContentFieldDto[] {
        return [
            ...groupBy.fields.map(f => this.buildGroupByFieldDto(f)),
            ...groupBy.aggregates.map(a => this.buildAggregateFieldDto(a)),
        ];
    }

    private static buildFieldDto(f: QlBuilderFieldModel): QlQueryContentFieldDto {
        return {name: f.name, as: f.as};
    }

    private static buildGroupByFieldDto(f: QlBuilderGroupByFieldModel): QlQueryContentFieldDto {
        if (f.dateGroup) {
            return {name: f.name, as: f.as, fieldType: 'dateGroup', dateGroupPrecision: f.dateGroup.precision};
        }
        return {name: f.name, as: f.as};
    }

    private static buildAggregateFieldDto(a: QlBuilderGroupByAggregateModel): QlQueryContentFieldDto {
        return {name: a.name, as: a.as, function: a.fn, fieldType: 'function'};
    }

    private static buildJoin(node: QlBuilderChildNode, isGroupBy: boolean, depth: number): QlJoinDto {
        switch (node.kind) {
            case 'join':
                return this.buildRegularJoin(node, isGroupBy, depth);
            case 'extended':
                return this.buildExtendedJoin(node);
            case 'sideCar':
                return this.buildSideCarJoin(node);
        }
    }

    private static buildRegularJoin(node: QlBuilderJoinNode, isGroupBy: boolean, depth: number): QlJoinDto {
        const dto: QlJoinDto = {
            type: isGroupBy ? 'groupBy' : 'entity',
            name: node.name,
            as: node.alias,
            joinType: node.joinType,
            fields: node.fields.map(f => this.buildFieldDto(f)),
            joins: this.orderChildren(node.children).map(child => this.buildJoin(child, isGroupBy, depth + 1)),
        };

        if (node.filters?.length) {
            dto.filterCriteria = node.filters;
        }
        if (node.relation === 'oneToN') {
            if (node.onlyFirst) {
                dto.onlyFirst = true;
                // S5: the back-reference FK is added automatically; a join sort is never emitted
                // (DISTINCT ON vs ORDER BY — fails in view AND simple pipe).
                const fk = this.onlyFirstForeignKeyField(node.name);
                if (fk && !dto.fields!.some(f => f.name === fk)) {
                    dto.fields!.push({name: fk});
                }
            } else if (node.sortings?.length) {
                dto.sortings = node.sortings;
            }
        }

        return dto;
    }

    /**
     * +/_ extended member. Only ATTRIBUTES are supported (spike S2: a `+` join merges as a raw,
     * label-less entity object that cannot be rendered as a cell); a model carrying a join-kind
     * node (resultKind 'entity') still compiles to the identical type:'meta' shape — the wire
     * format cannot tell the two apart — and is refused by precheckQlQuery(dto, model) with
     * `unknownJoin` (same code as the backend policy). Always a leaf: no fields, joins, filterCriteria, sortings,
     * joinParameters or parameters are ever set.
     */
    private static buildExtendedJoin(node: QlBuilderExtendedNode): QlJoinDto {
        const prefix = node.extendedKind === 'service' ? '+' : '_';
        return {
            type: 'meta',
            name: prefix + node.name,
            as: node.alias,
        };
    }

    /**
     * Bounded-external side-car flatten join. fields[].name is the BARE numeric typeFieldId as a
     * string — the ext_<id> form only ever appears on the RESULT row, never in the request
     * (matches QlQueryBuilder.boundedExternalVersionJoin, the existing hand-written helper for the
     * same join shape). Always a leaf: no joins, no filterCriteria.
     */
    private static buildSideCarJoin(node: QlBuilderSideCarNode): QlJoinDto {
        return {
            type: 'entity',
            name: node.instanceAlias,
            as: node.alias,
            joinType: 'left',
            joinParameters: {[node.scopeKey]: node.scopeId},
            fields: node.typeFieldIds.map(id => ({name: String(id)})),
        };
    }

    // ------------------------------------------------------------------
    // QlQueryDto -> model
    // ------------------------------------------------------------------

    /**
     * Best-effort reconstruction of a QlBuilderModel from a raw QlQueryDto — the JSON editor's
     * "JSON to Builder" re-import (requirement.md Playground). Every DTO feature the model cannot
     * represent is appended to unmappable as a human-readable (German) message instead of being
     * silently dropped; a non-empty unmappable list is a converter ERROR the caller must show, not
     * a warning to swallow.
     *
     * Known, permanent limitations (documented, not bugs):
     * - relation (nToOne vs. oneToN vs. manyToN) is inferred from wire-level HINTS only
     *   (EntityName#fk naming, onlyFirst, structureType) — the wire format does not carry the
     *   cardinality explicitly, and telling n:1 from m:n apart is not possible without entity
     *   metadata. Ambiguous joins default to nToOne; this is round-trip-safe for everything
     *   toQlQuery itself produces (it never emits the m:n case distinctly either).
     * - QlBuilderExtendedNode.resultKind cannot be determined from the DTO alone (attribute vs.
     *   join both compile to the identical type:'meta' shape) — reconstructed as 'attribute';
     *   callers that need the true kind must re-resolve it against meta.
     *
     * Filter criteria are carried over verbatim (query `filters` → root node, join `filterCriteria`
     * → that join node). A relative date filter (`e.createdDate GE ${D-TODAY-P30D}`) therefore comes
     * back as the identical criterion, which the builder node recognizes
     * ({@link QlBuilderRelativeDate.isRelativeCriterion}) and shows as a "Relativer Zeitraum" chip.
     * Whether a `${…}` value is admissible is decided by precheckQlQuery, not here.
     */
    static fromQlQuery(dto: QlQueryDto): QlBuilderFromQueryResult {
        const unmappable: string[] = [];
        // hasContent, not `?.length`: hand-written JSON may carry these as an OBJECT (`"variables": {"x": 1}`),
        // which `.length` reads as undefined and silently let through (QA #15)
        const has = QlBuilderQueryConverter.hasContent;

        if (has(dto.variables)) {
            unmappable.push('variables ist nicht abbildbar (verboten).');
        }
        if (has(dto.dependsOn)) {
            unmappable.push('dependsOn ist nicht abbildbar (verboten).');
        }
        if (has(dto.logics)) {
            unmappable.push('logics ist nicht abbildbar (verboten).');
        }
        if (dto.audit) {
            unmappable.push('audit ist nicht abbildbar (verboten).');
        }
        if (has(dto.selectAttributes)) {
            unmappable.push('selectAttributes wird vom Builder nicht unterstützt.');
        }
        if (has(dto.fixedAttributes)) {
            unmappable.push('fixedAttributes wird vom Builder nicht unterstützt.');
        }
        if (has(dto.fixedFilters)) {
            unmappable.push('fixedFilters wird vom Builder nicht unterstützt.');
        }
        const dtoAny = dto as unknown as { functionFilters?: unknown; fixedFunctionFilters?: unknown };
        if (has(dtoAny.functionFilters)) {
            unmappable.push('functionFilters ist nicht abbildbar (verboten).');
        }
        if (has(dtoAny.fixedFunctionFilters)) {
            unmappable.push('fixedFunctionFilters ist nicht abbildbar (verboten).');
        }

        if (!dto.start) {
            unmappable.push('start fehlt.');
            return {model: null, unmappable};
        }
        if (dto.start.type !== 'entity' && dto.start.type !== 'groupBy') {
            unmappable.push(`start.type '${dto.start.type}' wird nicht unterstützt (erlaubt: 'entity', 'groupBy').`);
            return {model: null, unmappable};
        }
        if (!dto.start.name || !dto.start.as) {
            unmappable.push('start.name/start.as fehlt.');
            return {model: null, unmappable};
        }
        if (has(dto.start.parameters)) {
            unmappable.push('start.parameters ist nicht abbildbar (verboten).');
        }
        if (has(dto.start.logics)) {
            unmappable.push('start.logics ist nicht abbildbar (verboten).');
        }

        const isGroupBy = dto.start.type === 'groupBy';

        let groupBy: QlBuilderGroupByModel | undefined;
        let rootFields: QlBuilderFieldModel[] = [];

        if (isGroupBy) {
            const fields: QlBuilderGroupByFieldModel[] = [];
            const aggregates: QlBuilderGroupByAggregateModel[] = [];

            for (const f of dto.start.fields ?? []) {
                if (!f.name) {
                    unmappable.push('groupBy-Feld ohne name.');
                    continue;
                }
                if (f.fieldType === 'function') {
                    if (!f.as || !f.function) {
                        unmappable.push(`Aggregatfeld '${f.name}' ohne as/function.`);
                        continue;
                    }
                    aggregates.push({name: f.name, as: f.as, fn: f.function as QlBuilderGroupByAggregateModel['fn']});
                } else if (f.fieldType === 'dateGroup') {
                    if (!f.as || !f.dateGroupPrecision) {
                        unmappable.push(`dateGroup-Feld '${f.name}' ohne as/dateGroupPrecision.`);
                        continue;
                    }
                    fields.push({name: f.name, as: f.as, dateGroup: {precision: f.dateGroupPrecision}});
                } else {
                    fields.push({name: f.name, as: f.as});
                }
            }

            groupBy = {fields, aggregates};
        } else {
            rootFields = (dto.start.fields ?? []).map(f => ({name: f.name ?? '', as: f.as}));
        }

        let extendedCount = 0;
        const bumpExtended = (): number => ++extendedCount;

        const children: QlBuilderChildNode[] = [];
        for (const j of dto.start.joins ?? []) {
            const child = this.fromJoinDto(j, isGroupBy, 1, unmappable, bumpExtended);
            if (child) {
                children.push(child);
            }
        }

        // query-level filters: root-only criteria are the node's "Start" filters, everything reaching into a
        // join alias (or an OR group mixing aliases) is a condition over all entities — never `e.j1.x`
        const {rootFilters, conditions} = this.splitQueryFilters(dto.filters, dto.start.as);

        const root: QlBuilderEntityNode = {
            kind: 'entity',
            alias: dto.start.as,
            entityName: dto.start.name,
            fields: rootFields,
            filters: rootFilters,
            children,
        };

        const model: QlBuilderModel = {
            root,
            sortings: dto.sortings,
            limit: dto.paging?.pageSize,
            groupBy,
        };
        if (conditions.length) {
            model.conditions = conditions;
        }

        return {model, unmappable};
    }

    /**
     * `true` when a DTO member carries anything the builder would have to drop: a non-empty array, an
     * object with at least one key, or any other truthy scalar. `null`/`undefined`/`[]`/`{}` are empty.
     */
    static hasContent(value: unknown): boolean {
        if (value == null) {
            return false;
        }
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        if (typeof value === 'object') {
            return Object.keys(value as object).length > 0;
        }
        return !!value;
    }

    private static fromJoinDto(
        j: QlJoinDto,
        parentIsGroupBy: boolean,
        depth: number,
        unmappable: string[],
        bumpExtended: () => number,
    ): QlBuilderChildNode | null {

        if (!j.name || !j.as) {
            unmappable.push('Join ohne name/as.');
            return null;
        }
        if (POLYMORPHIC_JOIN_PATTERN.test(j.name)) {
            unmappable.push(`Join '${j.name}' ist ein verbotener polymorpher #objectId-Join.`);
            return null;
        }
        if (QlBuilderQueryConverter.hasContent(j.parameters)) {
            unmappable.push(`Join '${j.as}': parameters ist nicht abbildbar (verboten).`);
        }
        if (QlBuilderQueryConverter.hasContent(j.logics)) {
            unmappable.push(`Join '${j.as}': logics ist nicht abbildbar (verboten).`);
        }

        if (j.type === 'meta') {
            if (j.joins?.length || j.filterCriteria?.length || j.sortings?.length || j.fields?.length) {
                unmappable.push(`Erweiterter Knoten '${j.as}' ist kein Blatt (verboten).`);
            }
            const isConfig = j.name.startsWith('_');
            const isService = j.name.startsWith('+');
            if (!isConfig && !isService) {
                unmappable.push(`Erweiterter Knoten '${j.as}' hat weder +- noch _-Präfix ('${j.name}').`);
                return null;
            }
            if (bumpExtended() > QL_BUILDER_POLICY_LIMITS.maxExtendedNodes) {
                const max = QL_BUILDER_POLICY_LIMITS.maxExtendedNodes;
                unmappable.push(`Höchstens ${max} erweitertes Feld je Definition erlaubt.`);
            }

            const extended: QlBuilderExtendedNode = {
                kind: 'extended',
                alias: j.as,
                name: j.name.substring(1),
                extendedKind: isService ? 'service' : 'config',
                // Not derivable from the wire shape alone — see class doc comment.
                resultKind: 'attribute',
            };
            return extended;
        }

        const isSideCar = !!j.joinParameters && Object.keys(j.joinParameters).length > 0;
        if (isSideCar) {
            if (j.joins?.length) {
                unmappable.push(`Seitenwagen-Join '${j.as}' hat verbotene Kind-Joins.`);
            }
            if (j.filterCriteria?.length) {
                unmappable.push(`Seitenwagen-Join '${j.as}' hat verbotene Filter.`);
            }

            const [scopeKey, scopeIdRaw] = Object.entries(j.joinParameters!)[0];
            const scopeId = Number(scopeIdRaw);
            if (isNaN(scopeId)) {
                unmappable.push(`Seitenwagen-Join '${j.as}' hat einen nicht-numerischen Scope-Wert.`);
            }

            const rawFieldNames = (j.fields ?? []).map(f => f.name ?? '');
            const typeFieldIds = rawFieldNames.map(n => Number(n)).filter(id => !isNaN(id) && id > 0);
            if (typeFieldIds.length !== rawFieldNames.length) {
                unmappable.push(`Seitenwagen-Join '${j.as}' hat Felder, die keine numerische typeFieldId sind.`);
            }

            const sideCar: QlBuilderSideCarNode = {
                kind: 'sideCar',
                alias: j.as,
                instanceAlias: j.name,
                scopeKey,
                scopeId,
                typeFieldIds,
            };
            return sideCar;
        }

        if (j.type !== 'entity' && j.type !== 'groupBy') {
            unmappable.push(`Join '${j.as}' hat unbekannten type '${j.type}'.`);
            return null;
        }
        if (parentIsGroupBy && j.type !== 'groupBy') {
            unmappable.push(`Join '${j.as}' muss im groupBy-Modus type:'groupBy' haben (war '${j.type}').`);
        }
        if (depth > QL_BUILDER_POLICY_LIMITS.maxJoinDepth) {
            const maxDepth = QL_BUILDER_POLICY_LIMITS.maxJoinDepth;
            unmappable.push(`Join '${j.as}' überschreitet die maximale Verschachtelungstiefe von ${maxDepth}.`);
        }

        const relation = this.inferRelation(j);
        if (relation === 'oneToN' && j.structureType === 'object') {
            unmappable.push(`Join '${j.as}' nutzt structureType 'object' auf einer 1:N-Relation (verboten).`);
        }

        const children: QlBuilderChildNode[] = [];
        for (const cj of j.joins ?? []) {
            const childIsGroupBy = parentIsGroupBy && j.type === 'groupBy';
            const child = this.fromJoinDto(cj, childIsGroupBy, depth + 1, unmappable, bumpExtended);
            if (child) {
                children.push(child);
            }
        }

        const node: QlBuilderJoinNode = {
            kind: 'join',
            alias: j.as,
            name: j.name,
            relation,
            joinType: (j.joinType as QlBuilderJoinType) ?? 'left',
            fields: (j.fields ?? []).map(f => ({name: f.name ?? '', as: f.as})),
            filters: j.filterCriteria ?? [],
            children,
        };
        if (relation === 'oneToN') {
            if (j.onlyFirst) {
                node.onlyFirst = true;
                if (j.sortings?.length) {
                    unmappable.push(
                        `Join '${j.as}': Sortierung ist bei „nur erster Eintrag“ nicht möglich (verboten).`);
                }
            } else if (j.sortings?.length) {
                node.sortings = j.sortings;
            }
        }

        return node;
    }

    /** See the "Known, permanent limitations" note on fromQlQuery. */
    private static inferRelation(j: QlJoinDto): QlBuilderJoinRelation {
        if (j.onlyFirst || j.name?.includes('#')) {
            return 'oneToN';
        }
        return 'nToOne';
    }

    // ------------------------------------------------------------------
    // derived facts
    // ------------------------------------------------------------------

    /**
     * true when the definition contains a structural id outside the allowed spots — a generic
     * field (g_<id>:...) or a side-car node (scope id + typeFieldIds) — per requirement.md
     * "Foundational Decisions" (O1: "Definitionen mit g_/Seitenwagen sind envBound"). FK filter
     * VALUES are explicitly excluded (signed portable, requirement.md Runde 2 point 7) — only
     * field/join SHAPE is inspected, never filter values.
     */
    static deriveEnvBound(model: QlBuilderModel): boolean {
        const walk = (node: QlBuilderNode): boolean => {
            if (node.kind === 'sideCar') {
                return true;
            }
            if (node.kind === 'extended') {
                return false;
            }
            if (node.fields.some(f => GENERIC_FIELD_PATTERN.test(f.name))) {
                return true;
            }
            return node.children.some(walk);
        };

        if (walk(model.root)) {
            return true;
        }
        if (model.groupBy) {
            if (model.groupBy.fields.some(f => GENERIC_FIELD_PATTERN.test(f.name))) {
                return true;
            }
            if (model.groupBy.aggregates.some(a => GENERIC_FIELD_PATTERN.test(a.name))) {
                return true;
            }
        }
        return false;
    }

    /**
     * true for a side-car OR a groupBy definition (api-contract.md "Q-A": no includeCount, no
     * paginator, "Anzahl nicht verfuegbar" shown instead). Side-car: pivot forces GROUP BY of
     * every host column, so a plain row count is not meaningful either.
     */
    static requiresNoCount(model: QlBuilderModel): boolean {
        if (model.groupBy) {
            return true;
        }
        const hasSideCar = (node: QlBuilderNode): boolean => {
            if (node.kind === 'sideCar') {
                return true;
            }
            if (node.kind === 'extended') {
                return false;
            }
            return node.children.some(hasSideCar);
        };
        return hasSideCar(model.root);
    }

    // ------------------------------------------------------------------
    // client-side pre-check (mirrors the backend policy for immediate UI feedback)
    // ------------------------------------------------------------------

    /**
     * Structural subset of UiQlWidgetDefinitionPolicy this pure function CAN decide from a
     * QlQueryDto alone, with the SAME violation codes the backend uses (api-contract.md
     * "Violation-Codes"). The backend stays authoritative — codes that need entity metadata or
     * persisted state are NOT checked here and are called out below:
     *
     * - unknownField / unknownJoin — needs entity metadata (does this attribute/join exist?).
     * - structuralId — needs metadata to tell a legitimate g_<id>/side-car id from a raw FK id
     *   smuggled into a regular field/filter.
     * - joinParameterKeyNotAllowed — needs the side-car discovery's allowedJoinParameterKeys.
     * - groupByRequired / mappingInvalid — need the paired widgetType/mapping from the
     *   save/validate request, which this function does not see.
     * - aliasImmutable / probeFailed — only meaningful against persisted state / an engine run.
     *
     * Engine-spike rules (api-contract.md "Spike-Ergebnisse (QL-1)") checked here:
     * - S2: at most {@link QL_BUILDER_POLICY_LIMITS.maxExtendedNodes} +/_ node (`tooManyExtendedJoins`);
     *   a `+` JOIN (entity result) is refused with `unknownJoin` on `<join>.name`, exactly as the
     *   backend does — the wire shape is the same as for an attribute, so this is only decidable
     *   when the builder `model` is handed in (the backend decides it from entity metadata).
     * - S2: a +/_ node in a definition that carries a side-car → `sideCarSiblingJoin` (the
     *   side-car forces the `simple` pipe, where extended nodes come nested/unserializable).
     * - S7: a regular join after a side-car join (depth-first order) → `sideCarSiblingJoin`.
     * - S3: a groupBy column `g_<id>:<suffix>` with any suffix other than `:value` →
     *   `objectGenericFieldInGroupBy` (`:id` fails too).
     * - S5 (same codes as UiQlWidgetDefinitionPolicy.checkOnlyFirst): `sortings` on an `onlyFirst`
     *   join (DISTINCT ON vs ORDER BY) → `unknownJoin` on `<join>.sortings`; a query-level sorting
     *   on an `onlyFirst` alias → `unknownJoin`; an `onlyFirst` reverse join `<Entity>#<fk>` that
     *   does not select `<fk>Id` → `unknownField` on `<join>.fields` (toQlQuery adds it itself, so
     *   only a hand-edited definition can hit this).
     *
     * Filter values (UiQlWidgetDefinitionPolicy#isAllowedFilterVariable, round 5 point 13): a value
     * containing `${` is `forbiddenVariables` unless it is EXACTLY a relative date
     * `${D-<BASE>[±P…]}` ({@link QlBuilderRelativeDate.isExpression}) on a date-typed field — checked
     * in `filters`, `fixedFilters` and every regular join's `filterCriteria`, incl. nested and/or
     * groups, `value`, `valueTo` and list elements. Everything else (`${E:ME_ID}`, a date expression
     * embedded in text, `P2W`, …) is refused without metadata. Whether the field is a DATE needs
     * metadata: pass `dateFields`; a field it resolves to `false` refuses the expression, `null`
     * (unknown) or no resolver leaves that decision to the backend. A criterion without a field
     * refuses it (the backend cannot type it either).
     *
     * Runs this check against ANY QlQueryDto — including one hand-edited in the Playground's JSON
     * tab — which is exactly how the negative preset (#8, injected parameters) is meant to be
     * caught before the definition is ever sent to /ql/query (requirement.md Geschaeftsregeln 8:
     * "Der Playground fuehrt nur policy-konforme Definitionen aus.").
     */
    static precheckQlQuery(dto: QlQueryDto,
                           model?: QlBuilderModel | null,
                           dateFields?: QlBuilderDateFieldResolver | null): QlBuilderViolation[] {
        const violations: QlBuilderViolation[] = [];

        if (QlBuilderQueryConverter.hasContent(dto.variables)) {
            violations.push({code: 'forbiddenVariables', path: 'variables', message: 'variables ist nicht erlaubt.'});
        }
        if (QlBuilderQueryConverter.hasContent(dto.dependsOn)) {
            violations.push({code: 'forbiddenDependsOn', path: 'dependsOn', message: 'dependsOn ist nicht erlaubt.'});
        }
        if (QlBuilderQueryConverter.hasContent(dto.logics)) {
            violations.push({code: 'forbiddenLogics', path: 'logics', message: 'logics ist nicht erlaubt.'});
        }
        if (dto.audit) {
            violations.push({code: 'forbiddenAudit', path: 'audit', message: 'audit ist nicht erlaubt.'});
        }

        const start = dto.start;
        if (!start) {
            return violations;
        }

        const isGroupBy = start.type === 'groupBy';

        if (QlBuilderQueryConverter.hasContent(start.parameters)) {
            violations.push({
                code: 'forbiddenParameters',
                path: 'start.parameters',
                message: 'parameters ist auf dem Start-Knoten nicht erlaubt.',
            });
        }
        if (QlBuilderQueryConverter.hasContent(start.logics)) {
            violations.push({
                code: 'forbiddenLogics',
                path: 'start.logics',
                message: 'logics ist auf dem Start-Knoten nicht erlaubt.',
            });
        }
        if ((start.fields?.length ?? 0) > QL_BUILDER_POLICY_LIMITS.maxFieldsPerNode) {
            violations.push({
                code: 'tooManyFields',
                path: 'start.fields',
                message: `Mehr als ${QL_BUILDER_POLICY_LIMITS.maxFieldsPerNode} Felder am Start-Knoten.`,
            });
        }

        if (!dto.paging || !dto.paging.active) {
            violations.push({code: 'pagingMissing', path: 'paging', message: 'Paging ist Pflicht.'});
        } else {
            const maxPaging = isGroupBy
                ? QL_BUILDER_POLICY_LIMITS.maxPagingGroupBy
                : QL_BUILDER_POLICY_LIMITS.maxPagingEntity;
            if (dto.paging.pageSize > maxPaging) {
                violations.push({
                    code: 'pagingTooLarge',
                    path: 'paging.pageSize',
                    message: `Seitengröße überschreitet das Limit von ${maxPaging}.`,
                });
            }
        }

        if (isGroupBy) {
            (start.fields ?? []).forEach((f, index) =>
                this.checkGroupByGenericField(f, `start.fields[${index}]`, violations));
        }

        let joinCount = 0;
        let extendedCount = 0;
        let sideCarSeen = false;
        const onlyFirstAliases = new Set<string>();
        const definitionHasSideCar = this.dtoHasSideCar(start.joins);
        const extendedJoinAliases = this.extendedJoinAliases(model);

        // alias classes for the query-level filter check (mirrors UiQlWidgetDefinitionPolicy): a filter may
        // name the root or a regular join alias, never an extended/side-car alias nor an onlyFirst subtree
        const filterable = new Set<string>(start.as ? [start.as] : []);
        const onlyFirstSubtree = new Set<string>();
        const extendedAliases = new Set<string>();
        const sideCarAliases = new Set<string>();

        const walkJoin = (j: QlJoinDto, path: string, depth: number, parentIsGroupBy: boolean, underOnlyFirst: boolean): void => {
            joinCount++;
            if (joinCount > QL_BUILDER_POLICY_LIMITS.maxJoins) {
                violations.push({
                    code: 'tooManyJoins',
                    path,
                    message: `Mehr als ${QL_BUILDER_POLICY_LIMITS.maxJoins} Joins in der Definition.`,
                });
            }
            if (depth > QL_BUILDER_POLICY_LIMITS.maxJoinDepth) {
                violations.push({
                    code: 'joinDepthExceeded',
                    path,
                    message: `Join-Tiefe überschreitet ${QL_BUILDER_POLICY_LIMITS.maxJoinDepth}.`,
                });
            }
            if (QlBuilderQueryConverter.hasContent(j.parameters)) {
                violations.push({
                    code: 'forbiddenParameters',
                    path: `${path}.parameters`,
                    message: 'parameters ist auf Joins nicht erlaubt.',
                });
            }
            if (QlBuilderQueryConverter.hasContent(j.logics)) {
                violations.push({
                    code: 'forbiddenLogics',
                    path: `${path}.logics`,
                    message: 'logics ist auf Joins nicht erlaubt.',
                });
            }
            if (j.name && POLYMORPHIC_JOIN_PATTERN.test(j.name)) {
                violations.push({
                    code: 'forbiddenPolymorphicJoin',
                    path,
                    message: `Polymorpher Join '${j.name}' ist nicht erlaubt.`,
                });
            }

            if (j.type === 'meta') {
                if (j.as) {
                    extendedAliases.add(j.as);
                }
                extendedCount++;
                if (extendedCount > QL_BUILDER_POLICY_LIMITS.maxExtendedNodes) {
                    violations.push({
                        code: 'tooManyExtendedJoins',
                        path,
                        message: `Höchstens ${QL_BUILDER_POLICY_LIMITS.maxExtendedNodes} erweitertes Feld je Definition `
                            + 'erlaubt.',
                    });
                }
                if ((j.joins?.length ?? 0) > 0 || (j.fields?.length ?? 0) > 0) {
                    violations.push({
                        code: 'extendedJoinNotLeaf',
                        path,
                        message: 'Erweiterter Knoten muss ein Blatt sein (keine Joins/Felder).',
                    });
                }
                if ((j.filterCriteria?.length ?? 0) > 0 || (j.sortings?.length ?? 0) > 0) {
                    violations.push({
                        code: 'extendedJoinFilterOrSort',
                        path,
                        message: 'Filter/Sortierung auf erweiterten Knoten sind nicht erlaubt.',
                    });
                }
                if (parentIsGroupBy) {
                    violations.push({
                        code: 'extendedJoinInGroupBy',
                        path,
                        message: 'Erweiterte Knoten sind in groupBy-Definitionen nicht erlaubt.',
                    });
                }
                if (j.as && extendedJoinAliases.has(j.as)) {
                    // same code/path as UiQlWidgetDefinitionPolicy.checkExtendedJoin (not extendedJoinNotLeaf:
                    // the node IS a leaf — the reason is its result shape)
                    violations.push({
                        code: 'unknownJoin',
                        path: `${path}.name`,
                        message: `'${j.name}' ist eine berechnete Verknüpfung; `
                            + 'im Widget sind nur berechnete Felder möglich.',
                    });
                }
                if (definitionHasSideCar) {
                    violations.push({
                        code: 'sideCarSiblingJoin',
                        path,
                        message: 'Erweiterte Felder und externe Zusatzfelder lassen sich nicht in einer '
                            + 'Definition kombinieren.',
                    });
                }
                return; // meta nodes are leaves — never recurse
            }

            const isSideCar = !!j.joinParameters && Object.keys(j.joinParameters).length > 0;
            if (isSideCar) {
                if (j.as) {
                    sideCarAliases.add(j.as);
                }
                if ((j.filterCriteria?.length ?? 0) > 0) {
                    violations.push({
                        code: 'sideCarFilter',
                        path,
                        message: 'Filter auf Seitenwagen-Feldern sind nicht erlaubt.',
                    });
                }
                if ((j.joins?.length ?? 0) > 0) {
                    violations.push({
                        code: 'sideCarChildJoin',
                        path,
                        message: 'Kind-Joins unter einem Seitenwagen-Join sind nicht erlaubt.',
                    });
                }
                if (parentIsGroupBy) {
                    violations.push({
                        code: 'sideCarInGroupBy',
                        path,
                        message: 'Seitenwagen sind in groupBy-Definitionen nicht erlaubt.',
                    });
                }
                if ((j.fields?.length ?? 0) > QL_BUILDER_POLICY_LIMITS.maxSideCarFields) {
                    violations.push({
                        code: 'tooManyFields',
                        path,
                        message: `Mehr als ${QL_BUILDER_POLICY_LIMITS.maxSideCarFields} Seitenwagen-Felder.`,
                    });
                }
                sideCarSeen = true;
                return; // side-car joins are leaves — never recurse
            }

            if (sideCarSeen) {
                violations.push({
                    code: 'sideCarSiblingJoin',
                    path,
                    message: 'Verknüpfungen müssen vor dem Seitenwagen-Join stehen.',
                });
            }
            const inOnlyFirst = underOnlyFirst || !!j.onlyFirst;
            if (j.as) {
                (inOnlyFirst ? onlyFirstSubtree : filterable).add(j.as);
            }
            if (j.onlyFirst) {
                if (j.as) {
                    onlyFirstAliases.add(j.as);
                }
                if ((j.sortings?.length ?? 0) > 0) {
                    violations.push({
                        code: 'unknownJoin',
                        path: `${path}.sortings`,
                        message: 'Bei „nur erster Eintrag“ kann die Verknüpfung nicht sortiert werden.',
                    });
                }
                const fk = this.onlyFirstForeignKeyField(j.name);
                if (fk && !(j.fields ?? []).some(f => f.name === fk && (!f.fieldType || f.fieldType === 'regular'))) {
                    violations.push({
                        code: 'unknownField',
                        path: `${path}.fields`,
                        message: `Bei „nur erster Eintrag“ muss das Feld '${fk}' ausgewählt sein.`,
                    });
                }
            }

            if (parentIsGroupBy && j.type !== 'groupBy') {
                violations.push({
                    code: 'groupByInconsistent',
                    path,
                    message: "Im groupBy-Modus müssen alle Joins type:'groupBy' haben.",
                });
            }
            if ((j.fields?.length ?? 0) > QL_BUILDER_POLICY_LIMITS.maxFieldsPerNode) {
                violations.push({
                    code: 'tooManyFields',
                    path,
                    message: `Mehr als ${QL_BUILDER_POLICY_LIMITS.maxFieldsPerNode} Felder am Join.`,
                });
            }
            if (parentIsGroupBy && j.type === 'groupBy') {
                (j.fields ?? []).forEach((f, index) =>
                    this.checkGroupByGenericField(f, `${path}.fields[${index}]`, violations));
            }
            this.checkFilterVariables(j.filterCriteria, `${path}.filterCriteria`, dateFields, violations);

            for (const cj of j.joins ?? []) {
                walkJoin(cj, `${path}.joins[]`, depth + 1, parentIsGroupBy && j.type === 'groupBy', inOnlyFirst);
            }
        };

        for (const j of start.joins ?? []) {
            walkJoin(j, 'start.joins[]', 1, isGroupBy, false);
        }

        const aliasClasses = {filterable, onlyFirstSubtree, extendedAliases, sideCarAliases};
        this.checkFilterVariables(dto.filters, 'filters', dateFields, violations);
        this.checkQueryFilterFields(dto.filters, 'filters', aliasClasses, violations);
        this.checkFilterVariables(dto.fixedFilters, 'fixedFilters', dateFields, violations);
        this.checkQueryFilterFields(dto.fixedFilters, 'fixedFilters', aliasClasses, violations);

        // S5: an onlyFirst join is a child unit — a query-level sorting on its alias cannot be rendered either
        (dto.sortings ?? []).forEach((sorting, index) => {
            const alias = sorting?.name?.split('.')[0];
            if (alias && onlyFirstAliases.has(alias)) {
                violations.push({
                    code: 'unknownJoin',
                    path: `sortings[${index}]`,
                    message: 'Nach einer Verknüpfung mit „nur erster Eintrag“ kann nicht sortiert werden.',
                });
            }
        });

        return violations;
    }

    /** S3: a grouping column may name a generic field only as `g_<id>:value`. Aggregates are not grouping columns. */
    private static checkGroupByGenericField(
        f: QlQueryContentFieldDto,
        path: string,
        violations: QlBuilderViolation[]): void {
        if (f.fieldType === 'function' || !f.name) {
            return;
        }
        const match = GENERIC_FIELD_PATTERN.exec(f.name);
        if (match && match[2] !== 'value') {
            violations.push({
                code: 'objectGenericFieldInGroupBy',
                path,
                message: `'${f.name}': Gruppiert werden kann nur nach ':value' eines einfachen generischen Felds.`,
            });
        }
    }

    /**
     * Mirrors UiQlWidgetDefinitionPolicy.checkFilters/checkFilterValue: every `${…}` filter value must be
     * a relative date on a date field. Paths follow the backend (`filters[0].value`, `….or[1].valueTo`).
     */
    private static checkFilterVariables(
        filters: FilterCriteria[] | null | undefined,
        path: string,
        dateFields: QlBuilderDateFieldResolver | null | undefined,
        violations: QlBuilderViolation[]): void {

        (filters ?? []).forEach((criterion, index) => {
            if (!criterion) {
                return;
            }
            const criterionPath = `${path}[${index}]`;
            const isDateField = this.resolveDateField(criterion.field, dateFields);
            this.checkFilterValue(criterion.value, isDateField, `${criterionPath}.value`, violations);
            this.checkFilterValue(criterion.valueTo, isDateField, `${criterionPath}.valueTo`, violations);
            this.checkFilterVariables(criterion.and, `${criterionPath}.and`, dateFields, violations);
            this.checkFilterVariables(criterion.or, `${criterionPath}.or`, dateFields, violations);
        });
    }

    /**
     * Mirrors the backend policy for the FIELD of every query-level filter leaf (incl. nested and/or groups):
     * the path must be exactly `<alias>.<field>` (`unknownField` otherwise, also for an unknown alias), an
     * extended (+/_) alias or member is `extendedJoinFilterOrSort`, a side-car alias or an `ext_<id>` column is
     * `sideCarFilter`, an `onlyFirst` join or anything below it is `unknownJoin` (a separate child unit the
     * WHERE of the root cannot reach). Paths follow the backend (`filters[1].or[0].field`).
     */
    private static checkQueryFilterFields(
        filters: FilterCriteria[] | null | undefined,
        path: string,
        aliases: { filterable: Set<string>; onlyFirstSubtree: Set<string>; extendedAliases: Set<string>; sideCarAliases: Set<string> },
        violations: QlBuilderViolation[]): void {

        (filters ?? []).forEach((criterion, index) => {
            if (!criterion) {
                return;
            }
            const criterionPath = `${path}[${index}]`;
            if (criterion.field) {
                this.checkQueryFilterField(criterion.field, `${criterionPath}.field`, aliases, violations);
            }
            this.checkQueryFilterFields(criterion.and, `${criterionPath}.and`, aliases, violations);
            this.checkQueryFilterFields(criterion.or, `${criterionPath}.or`, aliases, violations);
        });
    }

    private static checkQueryFilterField(
        field: string,
        path: string,
        aliases: { filterable: Set<string>; onlyFirstSubtree: Set<string>; extendedAliases: Set<string>; sideCarAliases: Set<string> },
        violations: QlBuilderViolation[]): void {

        const alias = this.filterAliasOf(field);
        if (!alias) {
            violations.push({
                code: 'unknownField',
                path,
                message: `Das Filterfeld '${field}' muss als <Alias>.<Feld> angegeben werden (z. B. e.status).`,
            });
            return;
        }
        const name = field.substring(alias.length + 1);
        if (aliases.sideCarAliases.has(alias) || name.startsWith('ext_')) {
            violations.push({
                code: 'sideCarFilter',
                path,
                message: `Filter auf externen Zusatzfeldern sind nicht erlaubt ('${field}').`,
            });
            return;
        }
        if (aliases.extendedAliases.has(alias) || name.startsWith('+') || name.startsWith('_')) {
            violations.push({
                code: 'extendedJoinFilterOrSort',
                path,
                message: `Filter auf erweiterten (berechneten) Feldern sind nicht erlaubt ('${field}').`,
            });
            return;
        }
        if (aliases.onlyFirstSubtree.has(alias)) {
            violations.push({
                code: 'unknownJoin',
                path,
                message: `Auf eine Verknüpfung mit „nur erster Eintrag“ (oder darunter) kann nicht gefiltert werden ('${field}').`,
            });
            return;
        }
        if (!aliases.filterable.has(alias)) {
            violations.push({
                code: 'unknownField',
                path,
                message: `Der Alias '${alias}' des Filterfelds '${field}' ist in der Abfrage nicht bekannt.`,
            });
        }
    }

    /** `true`/`false` from the resolver; `null` = unknown; a criterion without `<alias>.<field>` is never a date field. */
    private static resolveDateField(
        field: string | null | undefined,
        dateFields: QlBuilderDateFieldResolver | null | undefined): boolean | null {

        const dot = field?.indexOf('.') ?? -1;
        if (!field || dot <= 0 || dot === field.length - 1) {
            return false;
        }
        return dateFields ? dateFields(field.substring(0, dot), field.substring(dot + 1)) : null;
    }

    private static checkFilterValue(
        value: unknown,
        isDateField: boolean | null,
        path: string,
        violations: QlBuilderViolation[]): void {

        if (Array.isArray(value)) {
            value.forEach((element, index) => this.checkFilterValue(element, isDateField, `${path}[${index}]`, violations));
            return;
        }
        if (typeof value !== 'string' || !value.includes('${')) {
            return;
        }
        if (isDateField !== false && QlBuilderRelativeDate.isExpression(value)) {
            return;
        }
        violations.push({
            code: 'forbiddenVariables',
            path,
            message: `Die Variable '${value}' ist nicht erlaubt; zulässig sind nur relative Datumswerte \${D-…} auf Datumsfeldern.`,
        });
    }

    private static dtoHasSideCar(joins: QlJoinDto[] | undefined): boolean {
        return (joins ?? []).some(j =>
            (!!j.joinParameters && Object.keys(j.joinParameters).length > 0)
            || (j.type !== 'meta' && this.dtoHasSideCar(j.joins)));
    }

    /** Aliases of the model's extended nodes that are JOINS (resultKind 'entity') — invisible on the wire. */
    private static extendedJoinAliases(model: QlBuilderModel | null | undefined): Set<string> {
        const aliases = new Set<string>();
        const walk = (node: QlBuilderNode): void => {
            if (node.kind === 'extended') {
                if (node.resultKind === 'entity') {
                    aliases.add(node.alias);
                }
                return;
            }
            if (node.kind === 'sideCar') {
                return;
            }
            node.children.forEach(walk);
        };
        if (model?.root) {
            walk(model.root);
        }
        return aliases;
    }
}

/** Exported for reuse by callers that need to recognize a generic-field name outside this module. */
export const QlBuilderFieldPatterns = {
    generic: GENERIC_FIELD_PATTERN,
    eav: EAV_FIELD_PATTERN,
    forbiddenDtoName: GEN_DTO_NAME_PATTERN,
    polymorphicJoin: POLYMORPHIC_JOIN_PATTERN,
} as const;
